<template>
  <dropdown
    ref="dropdownRef"
    :is-disabled="isDisabled"
    title="Checklist Templates"
    class="checklist-template-picker"
    @open="handleDropdownOpen()"
  >
    <slot>
      <dd-icon-button
        :is-disabled="isDisabled"
        icon="plus"
        type="circle"
        size="medium"
        title="Add a checklist template to this status"
      />
    </slot>
    <template #content>
      <div>
        <sleek-filter-input
          v-if="showFilterInput"
          ref="sleekFilterInputRef"
          v-model="filterQuery"
          placeholder="Search by name..."
          show-small-text
        />
        <ul>
          <li
            v-for="template in filteredTemplates"
            :key="template.id"
            @click="selectTemplate(template.id)"
          >
            <span>{{ template.title }}</span>
            <status-chicklet
              v-if="statusFor(template)"
              :status="statusFor(template)"
              class="checklist-template-picker__status"
            />
          </li>
        </ul>
        <span v-if="!filteredTemplates.length">No templates found</span>
        <p v-if="hasTemplatesOnOtherStatuses" class="checklist-template-picker__hint">
          A template can only be on one status. Picking one that's on another status moves it here.
        </p>
      </div>
    </template>
  </dropdown>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
/* Mixins */
import SearchableDropdownMixin from '@/mixins/searchable-dropdown.mixin'

/* Data types */
import { IChecklistTemplate, IItemStatus } from '@/api/types/response.types'

export default defineComponent({
  name: 'ChecklistTemplatePicker',

  extends: SearchableDropdownMixin,

  props: {
    templates: {
      type: Array as () => IChecklistTemplate[],
      required: true,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    /* Optionally pass the workflow's statuses to show which status each template is already on */
    statuses: {
      type: Array as () => IItemStatus[],
      default: (): IItemStatus[] => [],
    },
  },

  emits: ['template-selected'],

  computed: {
    totalOptionsCount(): number {
      return this.templates.length
    },
    hasTemplatesOnOtherStatuses(): boolean {
      return this.templates.some(template => !!this.statusFor(template))
    },
    filteredTemplates(): IChecklistTemplate[] {
      if (this.filterQuery.length === 0) {
        return this.templates
      }

      return this.templates.filter(template =>
        template.title.toLowerCase().includes(this.filterQuery.toLowerCase()),
      )
    },
  },

  methods: {
    statusFor(template: IChecklistTemplate): IItemStatus {
      return this.statuses.find(s => s.id === template.statusID)
    },
    selectTemplate(id: number) {
      this.$emit('template-selected', id)
      const dropdown = this.$refs.dropdownRef as any
      dropdown.handleClose()
      this.clearFilterQuery()
    },
  },
})
</script>

<style lang="scss">
.checklist-template-picker {
  li {
    display: flex;
    align-items: center;
  }

  &__status {
    margin-left: auto;
    padding-left: 8px;
  }

  &__hint {
    max-width: 280px;
    margin: 8px 0 0;
    @include lettering--p-xs;
    color: var(--color-text-third);
    white-space: normal;
  }
}
</style>
